import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { useLanguage } from "../../contexts/LanguageContext";

const PAGE_SIZE = 6;

export default function ReviewsSection({ network, networkName, accentColor }) {
  const { t, field } = useLanguage();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    setReviews([]); setStats(null);
    api.get("/reviews/stats", { params: { network } }).then(({ data }) => setStats(data)).catch(() => {});
    api.get("/reviews", { params: { network, limit: PAGE_SIZE, skip: 0 } }).then(({ data }) => setReviews(data)).catch(() => {});
  }, [network]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await api.get("/reviews", { params: { network, limit: PAGE_SIZE, skip: reviews.length } });
      setReviews((prev) => [...prev, ...data]);
    } catch (_) { /* noop */ } finally { setLoadingMore(false); }
  };

  if (!stats || stats.count === 0) return null;
  const maxCount = Math.max(...Object.values(stats.distribution || {}), 1);
  const hasMore = reviews.length < stats.count;

  return (
    <section className="px-4 sm:px-6 lg:px-8 py-20" data-testid="network-reviews-section">
      <div className="mx-auto max-w-6xl">
        <div className="grid md:grid-cols-[280px_1fr] gap-10 mb-10">
          <div className="text-center md:text-left" data-testid="reviews-summary">
            <p className="font-heading text-5xl font-extrabold mb-1" data-testid="reviews-average-rating">{stats.average_rating}<span className="text-xl text-zinc-500">/5</span></p>
            <div className="flex gap-0.5 justify-center md:justify-start mb-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`w-4 h-4 ${i < Math.round(stats.average_rating) ? "fill-amber-400 text-amber-400" : "text-zinc-600"}`} />
              ))}
            </div>
            <p className="text-sm text-zinc-500" data-testid="reviews-total-count">
              {stats.count} {t("network.reviews_verified", "avis clients vérifiés")}
            </p>
          </div>
          <div className="space-y-1.5" data-testid="reviews-distribution">
            {[5, 4, 3, 2, 1].map((star) => {
              const count = stats.distribution?.[String(star)] || 0;
              const pct = maxCount ? (count / maxCount) * 100 : 0;
              return (
                <div key={star} className="flex items-center gap-2.5 text-xs text-zinc-500" data-testid={`reviews-distribution-bar-${star}`}>
                  <span className="w-6 shrink-0 flex items-center gap-0.5">{star}<Star className="w-3 h-3 fill-amber-400 text-amber-400" /></span>
                  <div className="flex-1 h-1.5 rounded-full bg-white/8 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: accentColor || "#818cf8" }} />
                  </div>
                  <span className="w-8 text-right shrink-0">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        <h2 className="font-heading text-xl font-bold mb-6">{t("network.reviews_title", "Avis clients")} {networkName}</h2>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {reviews.map((r, i) => (
            <motion.div
              key={r.id} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % PAGE_SIZE) * 0.06 }}
              className="rounded-2xl border border-white/5 bg-zinc-900/60 p-5" data-testid={`review-card-${r.id}`}
            >
              <div className="flex gap-0.5 mb-3">
                {Array.from({ length: r.rating }).map((_, idx) => <Star key={idx} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}
              </div>
              <p className="text-sm text-zinc-300 mb-4">"{field(r, "comment")}"</p>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-white">{r.author_name}</span>
                {r.created_at && <span className="text-[11px] text-zinc-500" data-testid={`review-date-${r.id}`}>{new Date(r.created_at).toLocaleDateString(t("common.locale", "fr-FR"))}</span>}
              </div>
            </motion.div>
          ))}
        </div>

        {hasMore && (
          <div className="text-center mt-10">
            <button
              onClick={loadMore} disabled={loadingMore}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold border border-white/15 hover:border-white/30 hover:bg-white/5 transition-colors disabled:opacity-60"
              data-testid="reviews-load-more-btn"
            >
              {loadingMore && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {t("network.reviews_load_more", "Voir plus d'avis")} ({reviews.length}/{stats.count})
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
